import { CTASection } from '../components/CTASection';
import { LeadForm } from '../components/LeadForm';
import { PageHero } from '../components/PageHero';
import { Section, SectionHeading, Reveal } from '../components/Section';
import { site } from '../data/site';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';

export function Contact() {
  return (
    <>
      <SEO
        title="Contact"
        description="Contact MaytoMation about software development, automation, infrastructure, networking, AI systems, managed hosting, and support in Massachusetts."
      />
      <PageHero
        eyebrow="Contact"
        title="Tell us what needs to work better."
        body="Use this form for project requests, automation ideas, infrastructure concerns, hosting questions, network cleanup, AI system planning, or long-term support conversations."
        primary={{ label: 'Book Consultation', href: '/booking' }}
        secondary={{ label: 'View Pricing', href: '/pricing' }}
        highlights={['Clear next steps', 'No hard sell', 'Practical scope', 'Massachusetts based']}
      />

      <Section>
        <div className="grid gap-10 lg:grid-cols-[.8fr_1.2fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow="Start Here"
              title="A useful request includes context, systems involved, and the outcome you want."
              body="MaytoMation will reply with clarifying questions, a practical recommendation, and whether the next step should be a consultation, audit, discovery sprint, or scoped proposal."
            />
            <div className="mt-8 grid gap-4">
              <ContactMethod icon="mail" title="Email" body={site.email} />
              <ContactMethod icon="phone" title="Phone" body={site.phone} />
              <ContactMethod icon="mapPin" title="Service Area" body="Massachusetts and remote-friendly support" />
            </div>
            <div className="mt-8 rounded-lg border border-brand-border bg-white p-6 shadow-card">
              <h2 className="text-lg font-extrabold text-brand-dark">What happens after you send it?</h2>
              <div className="mt-4 grid gap-3 text-sm leading-7 text-brand-muted">
                <p>1. MaytoMation reviews the request and systems involved.</p>
                <p>2. You receive clarifying questions or a consultation link.</p>
                <p>3. If there is a fit, the next step is scoped around outcome, risk, timeline, and support needs.</p>
              </div>
            </div>
          </div>
          <Reveal>
            <LeadForm formType="contact" title="Project Contact Form" />
          </Reveal>
        </div>
      </Section>

      <CTASection title="Prefer to start with a consultation request?" button="Open Booking Form" />
    </>
  );
}

function ContactMethod({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="flex gap-4 rounded-lg border border-brand-border bg-white p-5 shadow-card">
      <Icon name={icon} className="mt-1 h-5 w-5 shrink-0 text-brand-teal" />
      <div>
        <h2 className="text-sm font-extrabold uppercase tracking-[0.14em] text-brand-navy">{title}</h2>
        <p className="mt-1 text-sm font-semibold text-brand-muted">{body}</p>
      </div>
    </div>
  );
}
